import { memo, useCallback, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import {
  FormSection,
  FormSwitch,
  FormTextFieldInlineEdit,
  List,
  ListItem,
} from '@common-components';
import { useFormProvider, useIsMobile, useProcessNewLabels } from 'hooks';
import useUpdateNodeFormData from 'pages/app-pages/chatbot-builder-page/hooks/useUpdateNodeFormData';
import FormLabelsSelector from 'pages/app-pages/chatbot-builder-page/nodes/components/steps/form-components/FormLabelsSelector';
import NodesFormTextEditor from 'pages/app-pages/chatbot-builder-page/nodes/components/steps/form-components/NodesFormTextEditor';
import StepFormFieldsWrapper from 'pages/app-pages/chatbot-builder-page/nodes/components/steps/form-components/StepFormFieldsWrapper';
import StepFormSubmitButtons from 'pages/app-pages/chatbot-builder-page/nodes/components/steps/form-components/StepFormSubmitButtons';
import StepFormWrapper from 'pages/app-pages/chatbot-builder-page/nodes/components/steps/form-components/StepFormWrapper';
import { WebviewButtonLinkStepFormFields } from 'pages/app-pages/chatbot-builder-page/nodes/components/steps/webview-button-link/types';
import {
  defaultWebviewButtonLinkStepFormValues,
  webviewButtonLinkFormFieldsConfig,
  webviewButtonLinkStepFormSchema,
} from 'pages/app-pages/chatbot-builder-page/nodes/components/steps/webview-button-link/utils';
import WebviewButtonLinkFormField from 'pages/app-pages/chatbot-builder-page/nodes/components/steps/webview-button-link/WebviewButtonLinkFormField';
import { Mention } from 'types';
import { characterLimitValue } from 'utils';

interface WebviewButtonLinkProps {
  id: string;
  defaultValues?: Partial<WebviewButtonLinkStepFormFields>;
  mentions?: Mention[];
  commands?: Mention[];
}

const WebviewButtonLinkStep = ({
  defaultValues,
  mentions,
  commands,
  id,
}: WebviewButtonLinkProps) => {
  const [localDefaultValues, setLocalDefaultValues] = useState({
    ...defaultWebviewButtonLinkStepFormValues,
    ...defaultValues,
    nodeId: defaultValues?.nodeId || uuid(),
    link: defaultValues?.link || {
      url: '',
      text: '',
      includeProjectParams: false,
    },
    labels: defaultValues?.labels || [],
  });

  const onLocalDefaultValuesChange = useCallback(
    (newDefaultValues: Partial<WebviewButtonLinkStepFormFields>) => {
      setLocalDefaultValues((prev) => ({
        ...prev,
        ...newDefaultValues,
      }));
    },
    [],
  );

  const formMethods = useFormProvider<WebviewButtonLinkStepFormFields>({
    schema: webviewButtonLinkStepFormSchema,
    defaultValues: localDefaultValues,
  });

  const updateNodeFormData = useUpdateNodeFormData();

  const { isMobile } = useIsMobile();

  const localCommands = useRef(commands);
  const localMentions = useRef(mentions);

  const processNewLabels = useProcessNewLabels();

  const { reset, formState } = formMethods;

  const handleOnSubmit = async (formData: WebviewButtonLinkStepFormFields) => {
    const labelIds = await processNewLabels(formData.labels);

    await updateNodeFormData({
      id,
      formData: {
        ...formData,
        labels: labelIds,
      },
    });
  };

  const { name, labels, input, optionFullWidth } = webviewButtonLinkFormFieldsConfig;

  return (
    <StepFormWrapper formMethods={formMethods} handleOnSubmit={handleOnSubmit}>
      <StepFormFieldsWrapper>
        <FormTextFieldInlineEdit {...name} variant='standard' size='small' />
        <FormLabelsSelector {...labels} onLocalDefaultValuesChange={onLocalDefaultValuesChange} />

        <NodesFormTextEditor
          characterLimit={characterLimitValue}
          autoFocus={!isMobile}
          {...input}
          mentions={localMentions.current}
          commands={localCommands.current}
        />
        <FormSection title='Webview Button'>
          <WebviewButtonLinkFormField />
        </FormSection>
        <List divider label='Additional Options'>
          <ListItem>
            <FormSwitch {...optionFullWidth} />
          </ListItem>
        </List>
      </StepFormFieldsWrapper>
      <StepFormSubmitButtons formState={formState} reset={reset} />
    </StepFormWrapper>
  );
};

export default memo(WebviewButtonLinkStep);
